import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "container",

        "optionsContainer",
        "optionsInput",

        "searchContainer",
        "searchInput",
        "searchOutput",
        "searchErrorMessageOutput",

        "formContainer",
        "formLine1Output",
        "formLine2Output",
        "formLine3Output",
        "formTownOutput",
        "formCountyOutput",
        "formPostcodeOutput", 
        "formThoroughfareOutput",
        "formBuildingNameOutput",
        "formBuildingNumberOutput",
        "formSubBuildingNameOutput",
        "formLongitudeOutput",
        "formLatitudeOutput",
    ]

    #formFieldsEditableAfterSelection = [
        "formLine1Output",
        "formLine2Output",
        "formLine3Output",
    ]

    #formFieldsRequired = [
        "formLine1Output",
        "formTownOutput",
        "formPostcodeOutput",
    ]

    connect() {
        if (this.hasOptionsInputTarget && this.optionsInputTarget.value !== "other") {
            this.#setSearchContainerVisible(false)
        }

        this.dispatch("connected", {})
    }

    interviewLocationSelected(event) {
        // this will be called when a interview-location controller broadcasts a change of location

        const inPerson = event.detail.location === "in_person"

        this.#setContainerVisible(inPerson)

        for (let field of this.#formFieldsRequired) {
            this[field + "Target"].required = inPerson
        }
    }

    optionSelected(event) {
        this.#fillForm(null)

        if (this.optionsInputTarget.value === "other") {
            this.#setSearchContainerVisible(true)
            return
        }

        this.#setSearchContainerVisible(false)

        if (this.optionsInputTarget.value === "") {
            return
        }

        this.#fillForm(JSON.parse(this.optionsInputTarget.value))
    }

    searchResultSelected(event) {
        this.#fillForm(null)

        if (this.searchOutputTarget.value === "") {
            return
        }
        
        this.#fillForm(JSON.parse(this.searchOutputTarget.value))
    }

    readonlyInput(event) {
        // custom implementation of readonly to ensure required fields block form submission

        if (event.currentTarget.dataset.readonly === "true") {
            event.currentTarget.value = ""
        }
    }

    async search(event) {
        const query = this.searchInputTarget.value.trim()
        if (query.length < 5) {
            this.#setSearchErrorVisible("Input too short")
            return
        }

        try {
            const response = await fetch(
                `/addresses/lookup?postcode=${encodeURIComponent(query)}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json"
                    }
                }
            )
            const json = await response.json()

            if (!response.ok) {
                throw new Error(`Lookup failed (${json.error})`)
            }

            this.searchOutputTarget.innerHTML = `<option selected="selected" value="">Select your address</option>`

            for (let address of json?.addresses) {
                const option = document.createElement("option")
                option.value = JSON.stringify(address)
                option.textContent = `${address.line1}, ${address.town}, ${address.county}, ${address.postcode}`

                this.searchOutputTarget.appendChild(option)
            }
        } catch (error) {
            this.#setSearchErrorVisible(error)
        }
    }

    #fillForm(address) {
        this.#setEditableFormFieldsReadonly(this.searchOutputTarget.value === "")

        this.formLine1OutputTarget.value = address?.line1 || ""
        this.formLine2OutputTarget.value = address?.line2 || ""
        this.formLine3OutputTarget.value = address?.line3 || ""
        this.formTownOutputTarget.value = address?.town || ""
        this.formCountyOutputTarget.value = address?.county || ""
        this.formPostcodeOutputTarget.value = address?.postcode || ""

        this.formThoroughfareOutputTarget.value = address?.thoroughfare || ""
        this.formBuildingNameOutputTarget.value = address?.building_name || ""
        this.formBuildingNumberOutputTarget.value = address?.building_number || "" 
        this.formSubBuildingNameOutputTarget.value = address?.sub_building_name || ""

        this.formLongitudeOutputTarget.value = address?.long || "" 
        this.formLatitudeOutputTarget.value = address?.lat || ""

        console.info(`new address: `, address)
    }

    #setSearchContainerVisible(visible) {
        const displayStyle = visible ? "inherit" : "none"
        this.searchContainerTarget.style.display = displayStyle
        this.formContainerTarget.style.display = displayStyle
    }

    #setSearchErrorVisible(error) {
        this.searchOutputTarget.innerHTML = ""
        this.searchErrorMessageOutputTarget.innerText = error
    }

    #setContainerVisible(visible) {
        this.containerTarget.style.display = visible ? "inherit" : "none"
    }

    #setEditableFormFieldsReadonly(readonly) {
        // editable fields are set as readonly if the selected search result is placeholder otherwise they become editable

        for (let field of this.#formFieldsEditableAfterSelection) {
            this[field + "Target"].dataset.readonly = readonly
        }
    }
}
